import {
  faCheck,
  faListUl,
  faShareNodes,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useMemo, useState } from "react";
import { Spinner } from "react-bootstrap";
import { Helmet } from "react-helmet-async";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import images from "../../assets/images";
import Header from "../../components/header";
import ShareModal from "../../components/modal/ShareModal";
import { useTranslate } from "../../context/TranslationContext";
import usePageTheme from "../../hooks/usePageTheme";
import { useGetInstructorsQuery } from "../../redux/Apis/User";
import {
  useCreateUserFollowerMutation,
  useGetFollowersByUserIdQuery,
  useUpdateFollowerMutation,
} from "../../redux/Apis/UserFollower";
import { createThemeVars, THEME_OPTIONS } from "../../utils/theme";
import "./style.css";

const InstructorFollower = () => {
  const { t } = useTranslate("instructorDetails");
  const { id } = useParams();
  const navigate = useNavigate();
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const { data: instructorData } = useGetInstructorsQuery(
    { id, params: {} },
    { refetchOnMountOrArgChange: true, skip: !id },
  );
  const creatorTheme =
    instructorData?.data?.theme || instructorData?.data?.user?.theme;
  const { selectedTheme, setSelectedTheme, currentTheme, pageThemeVars } =
    usePageTheme({ defaultTheme: creatorTheme });
  const { user } = useSelector((state) => state.authSlice);
  const [localFollowingMap, setLocalFollowingMap] = useState(new Map());
  const themeVars = useMemo(
    () => createThemeVars(currentTheme),
    [currentTheme],
  );
  const isLightTheme = useMemo(
    () => currentTheme?.isLight === true,
    [currentTheme],
  );

  // ---------------- API ----------------
  const {
    data: followersData,
    isLoading,
    isError,
    refetch,
  } = useGetFollowersByUserIdQuery({
    params: { userid: id },
  });

  const { data: myFollowingData, refetch: refetchMyFollowing } =
    useGetFollowersByUserIdQuery(
      { params: { created_by: user?._id } },
      { skip: !user?._id },
    );

  const [createUserFollower] = useCreateUserFollowerMutation();
  const [updateFollower] = useUpdateFollowerMutation();

  // ---------------- INIT FOLLOW STATE ----------------
  useEffect(() => {
    if (myFollowingData?.data) {
      const map = new Map();
      myFollowingData.data.forEach((item) => {
        map.set(item.user_id, item._id);
      });
      setLocalFollowingMap(map);
    }
  }, [myFollowingData]);

  const user_image = useMemo(() => {
    if (!user?.picture) return images.profile.placeholder;
    if (user.picture.startsWith("http")) return user.picture;
    return user.picture.startsWith("images/")
      ? `${process.env.REACT_APP_S3_BASE_URL}${user.picture}`
      : images.profile.placeholder;
  }, [user?.picture]);

  // ---------------- HANDLERS ----------------
  const handleFollowerClick = (follower) => {
    if (follower.role === "learner") {
      navigate(`/learner/${follower.username}`);
    } else {
      navigate(`/instructor/${follower.username}`);
    }
  };

  const handleFollow = async (follower) => {
    if (!user?._id) {
      toast.error(t("toast.loginFirst"));
      return;
    }

    const targetUserId = follower._id || follower.created_by;
    const existingFollowerId = localFollowingMap.get(targetUserId);

    try {
      if (existingFollowerId) {
        // UNFOLLOW
        setLocalFollowingMap((prev) => {
          const map = new Map(prev);
          map.delete(targetUserId);
          return map;
        });

        await updateFollower({
          id: existingFollowerId,
          payload: { follow: false },
        });

        toast.success(t("toast.unfollowSuccess"));
      } else {
        // FOLLOW
        const res = await createUserFollower({
          payload: {
            user: targetUserId,
            created_by: user._id,
            follow: true,
          },
        });

        const newId = res?.data?._id;

        setLocalFollowingMap((prev) => {
          const map = new Map(prev);
          map.set(targetUserId, newId);
          return map;
        });

        toast.success(t("toast.followSuccess"));
      }

      refetch();
      refetchMyFollowing();
    } catch (error) {
      console.log("error", error?.message, error?.data?.message);
      toast.error(
        error.message || error.data?.message || "Something went wrong",
      );
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success(t("toast.copySuccess"));
    } catch (error) {
      toast.error(t("toast.copyFailed"));
    }
  };

  const handleSharePost = () => {
    const url = window.location.href;

    setShareUrl(url);
    setShowShareModal(true);
  };

  const handleCopyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success(t("copySuccess"));
    } catch {
      toast.error(t("copyError"));
    }
  };

  const shareWhatsapp = () => {
    window.open(
      `https://wa.me/?text=${encodeURIComponent(shareUrl)}`,
      "_blank",
    );
  };

  const shareTwitter = () => {
    window.open(
      `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}`,
      "_blank",
    );
  };

  const shareLinkedIn = () => {
    window.open(
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
        shareUrl,
      )}`,
      "_blank",
    );
  };

  const shareInstagram = async () => {
    await navigator.clipboard.writeText(shareUrl);

    window.location.href = "instagram://app";

    setTimeout(() => {
      window.open("https://www.instagram.com/", "_blank");
    }, 1000);
  };

  const shareFacebook = () => {
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
      "_blank",
    );
  };

  // ---------------- UI ----------------
  return (
    <>
      <Helmet>
        <title>Creator's Followers | Traineego</title>
        <meta name="robots" content="index,follow" />
        <link rel="canonical" href="https://traineego.com/instructors" />
        <meta
          name="description"
          content="Explore followers and community members connected with this Creator on Traineego."
        />
      </Helmet>
      <Header
        showThemeToggle={true}
        themeOptions={THEME_OPTIONS}
        activeTheme={selectedTheme}
        onThemeChange={setSelectedTheme}
      />

      <div
        className={`min-vh-100 instructor-theme-page ${isLightTheme ? "instructor-theme-light" : ""}`}
        style={{
          padding: "150px 0 120px 0",
          ...pageThemeVars,
        }}
      >
        <h3 className="text-center mb-4">{t("followers")}</h3>

        {isLoading && (
          <p className="text-center">
            <Spinner />
          </p>
        )}

        {!isLoading && (!followersData?.data?.length || isError) && (
          <p className="text-center text-muted">{t("noFollowersFound")}</p>
        )}

        <div className="justify-content-center justify-items-center d-flex flex-wrap gap-3">
          <div className="col-lg-8">
            {followersData?.data?.map((follower) => {
              const fullName = `${follower?.first_name || ""} ${
                follower?.last_name || ""
              }`.trim();

              const initials = `${follower?.first_name?.[0] || ""}${
                follower?.last_name?.[0] || ""
              }`;

              const targetUserId = follower._id || follower.created_by;
              const isFollowing = localFollowingMap.has(targetUserId);
              const isOwn = user?._id === follower?.created_by;
              const isVerified =
                (follower?.verification_status ||
                  follower?.creator_obj?.verification_status) === "verified";

              return (
                <div
                  key={follower._id}
                  onClick={() => handleFollowerClick(follower)}
                  className="d-flex align-items-center justify-content-between p-3 mb-3 rounded follower-row"
                  style={{
                    background: isLightTheme ? "#fff" : "#0e0e18",
                    cursor: "pointer",
                    color: isLightTheme ? "#000" : "#fff",
                    border: "none",
                    boxShadow: `
                      inset 0 1px 0 rgba(255, 255, 255, 0.14),
                      0 8px 20px rgba(0, 0, 0, 0.28)
                    `,
                  }}
                >
                  <div className="follower-row-info">
                    <div className="follower-row-avatar">
                      {follower?.picture ? (
                        <img
                          src={follower.picture}
                          alt={fullName}
                          className="rounded-circle"
                          width="45"
                          height="45"
                          style={{ objectFit: "cover" }}
                        />
                      ) : (
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center"
                          style={{
                            width: 45,
                            height: 45,
                            background: "#2a2a40",
                            color: "#fff",
                          }}
                        >
                          {initials || "U"}
                        </div>
                      )}
                    </div>

                    <div className="follower-row-text">
                      <p className="mb-0 follower-row-name d-flex align-items-center gap-2">
                        <span className="follower-row-name-text">
                          {fullName}
                        </span>
                        {isVerified && (
                          <span className="follower-name-verified">
                            <FontAwesomeIcon
                              icon={faCheck}
                              style={{ fontSize: "10px" }}
                            />
                          </span>
                        )}
                      </p>
                      <p
                        className="mb-0 text-muted follower-row-tagline"
                        style={{ fontSize: 14 }}
                      >
                        {follower?.tagline || ""}
                      </p>
                    </div>
                  </div>

                  {!isOwn && (
                    <button
                      className={`follower-row-action ${
                        isFollowing
                          ? `site-btn ${isLightTheme ? "light-following-btn" : "border-btn"}`
                          : "site-btn"
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleFollow(follower);
                      }}
                    >
                      {isFollowing ? t("following") : t("follow")}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="sticky-action-wrapper" style={themeVars}>
        <div className="sticky-action-bar">
          <button className="action-btn" onClick={() => navigate("/")}>
            <i className="fas fa-home" />
          </button>

          <button className="action-btn" onClick={() => navigate("/community")}>
            <FontAwesomeIcon icon={faListUl} />
          </button>

          <button
            className={`action-btn ${showShareModal ? "active" : ""}`}
            onClick={() => handleSharePost()}
          >
            <FontAwesomeIcon icon={faShareNodes} />
          </button>

          {user && (
            <button
              className="action-btn profile-btn"
              onClick={() => {
                user?.role === "learner"
                  ? navigate("/learnerprofile", {
                      state: {
                        userProfileId: user?._id,
                        username: user?.username,
                      },
                    })
                  : navigate("/profile", {
                      state: {
                        userProfileId: user?._id,
                        username: user?.username,
                      },
                    });
              }}
            >
              <img src={user_image} alt="" className="profile-pic" />
            </button>
          )}
        </div>
      </div>

      {/* Share Modal */}
      <ShareModal
        show={showShareModal}
        onHide={() => setShowShareModal(false)}
        t={t}
        shareWhatsapp={shareWhatsapp}
        shareInstagram={shareInstagram}
        shareFacebook={shareFacebook}
        shareLinkedIn={shareLinkedIn}
        shareTwitter={shareTwitter}
        handleCopyShareLink={handleCopyShareLink}
        themeVars={themeVars}
      />
    </>
  );
};

export default InstructorFollower;
